import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import emailjs from "@emailjs/browser";
import instagramIcon from '../assets/images/instagram_blue.svg';
import "../Components/CreatorForm.css";

function SupportPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { register, handleSubmit, reset } = useForm();

  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'Support - TLINK.ME';
    window.scrollTo(0, 0);
    
    return () => {
      document.title = originalTitle;
    };
  }, []);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const templateParams = {
        from_name: data.name,
        from_email: data.email,
        to_name: "Support Team",
        message: `
          Support Request
          
          Name: ${data.name}
          Email: ${data.email}
          
          Message:
          ${data.message}
        `,
      };

      await emailjs.send(
        "service_ljsv2zh",
        "template_u71so5t",
        templateParams,
        "J2G5UGnV7bpNJ0-2p"
      );

      setIsSuccess(true);
      reset();
    } catch (error) {
      console.error("Error details:", error);
      alert("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <style>{`
        .support-info {
          width: 100%;
          text-align: left;
          margin-bottom: 2rem;
        }
        .support-info h2 {
          font-size: 1rem;
          font-weight: 600;
          color: #1E1E1E;
          margin: 1.5rem 0 0.75rem 0;
          letter-spacing: -0.02em;
        }
        .support-info p {
          font-size: 0.875rem;
          color: #666;
          margin: 0 0 0.5rem 0;
          line-height: 1.6;
        }
        .support-info ul {
          margin: 0;
          padding-left: 1.25rem;
        }
        .support-info li {
          font-size: 0.875rem;
          color: #666;
          margin-bottom: 0.375rem;
          line-height: 1.5;
        }
        .support-info a {
          color: #53A4E1;
          text-decoration: none;
        }
        .support-info a:hover {
          text-decoration: underline;
        }
        .support-email {
          color: #53A4E1;
          font-weight: 600;
        }
        .support-divider {
          width: 100%;
          height: 1px;
          background: #E5E7EB;
          margin: 2rem 0;
        }
        textarea {
          width: 100%;
          box-sizing: border-box;
          padding: 0.875rem 1.25rem;
          border: 1px solid #E5E7EB;
          border-radius: 16px;
          font-size: 0.875rem;
          font-family: 'Lexend', sans-serif;
          transition: all 0.2s ease;
          background: #ffffff;
          resize: vertical;
          min-height: 120px;
        }
        textarea:focus {
          outline: none;
          border-color: #53A4E1;
          box-shadow: 0 0 0 4px rgba(83, 164, 225, 0.1);
        }
        @media (max-width: 480px) {
          textarea {
            font-size: 16px;
            min-height: 100px;
          }
        }
      `}</style>

      <header className="site-header">
        <div className="header-content">
          <div className="header-logo">
            <Link to="/" className="logo-link">
              <h1>TLINK.ME</h1>
            </Link>
          </div>
        </div>
      </header>

      <div className="creator-form-container">
        {!isSuccess ? (
          <>
            <h1>Support</h1>
            <p>We're here to help. Get in touch with our team.</p>

            <div className="support-info">
              <h2>Response Time</h2>
              <p>Our team typically responds within 24-48 hours.</p>

              <h2>Contact Us Directly</h2>
              <p>Email: <a href="mailto:support@tlink.me" className="support-email">support@tlink.me</a></p>

              <h2>What We Can Help With</h2>
              <ul>
                <li>Account issues and access problems</li>
                <li>Payment and subscription questions</li>
                <li>Technical issues with the app</li>
                <li>Privacy and data requests</li>
                <li>General inquiries</li>
              </ul>

              <p style={{ marginTop: '1rem' }}>
                For privacy-related inquiries, please review our <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            </div>

            <div className="support-divider"></div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-field">
                <label>Full Name</label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  {...register("name", { required: true })}
                />
              </div>

              <div className="form-field">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  {...register("email", { required: true })}
                />
              </div>

              <div className="form-field">
                <label>Message</label>
                <textarea
                  placeholder="How can we help you?"
                  {...register("message", { required: true })}
                />
              </div>

              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "SENDING..." : "Send Message"}
              </button>
            </form>
          </>
        ) : (
          <div className="success-message">
            <h2>Message Sent!</h2>
            <p>Thank you for reaching out. Our support team will get back to you within 24-48 hours.</p>
          </div>
        )}
      </div>

      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span className="footer-title">FOLLOW US</span>
            <div className="footer-social">
              <img src={instagramIcon} alt="Instagram" className="footer-instagram-icon" />
              <a href="https://instagram.com/tlink.me" target="_blank" rel="noopener noreferrer" className="footer-handle">TLINK.ME</a>
            </div>
          </div>
          <div className="footer-center">
            <div className="footer-logo">TLINK.ME</div>
          </div>
          <div className="footer-right">
            <div className="footer-copyright">
              ALL RIGHTS RESERVED.
              <span className="footer-year">2025</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default SupportPage;

